import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { MultiPick } from "@/components/MultiPick";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</Label>
    {children}
  </div>
);

const Pick = ({ value, onChange, items, testId, placeholder, labels, allLabel = "All" }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger data-testid={testId} className="bg-white">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="bg-white">
      <SelectItem value="all">{allLabel}</SelectItem>
      {items.map((o) => (
        <SelectItem key={o} value={o}>{labels?.[o] || o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const FiltersBar = ({ searchParams, setSearchParams, showHistoryToggle = false, groupMode = false }) => {
  const [opts, setOpts] = useState({ organizations: [], genders: [], races: [], cities: [], states: [] });
  const get = (k) => searchParams.get(k) || "";
  const set = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "" || v === null) next.delete(k);
    else next.set(k, v);
    setSearchParams(next, { replace: true });
  };
  const setMulti = (k, vals) => {
    const next = new URLSearchParams(searchParams);
    next.delete(k);
    vals.forEach((v) => next.append(k, v));
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/filters/options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="filters-bar" className="no-print rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 xl:grid-cols-8 items-end">
        <div className="col-span-2">
          <Field label="Search name / email">
            <Input data-testid="filter-search-input" value={get("q")} onChange={(e) => set("q", e.target.value)} placeholder="Type a name…" />
          </Field>
        </div>
        <Field label="From">
          <Input data-testid="filter-start-date" type="date" value={get("start")} onChange={(e) => set("start", e.target.value)} />
        </Field>
        <Field label="To">
          <Input data-testid="filter-end-date" type="date" value={get("end")} onChange={(e) => set("end", e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Church / Org (select several)">
            <MultiPick testId="filter-org-multiselect" values={searchParams.getAll("organization")} onChange={(v) => setMulti("organization", v)} items={opts.organizations} allLabel="All churches / orgs" />
          </Field>
        </div>
        <Field label="City">
          <MultiPick testId="filter-city-multiselect" values={searchParams.getAll("city")} onChange={(v) => setMulti("city", v)} items={opts.cities || []} />
        </Field>
        <Field label="State">
          <MultiPick testId="filter-state-multiselect" values={searchParams.getAll("state")} onChange={(v) => setMulti("state", v)} items={opts.states || []} />
        </Field>
        <Field label="Assessment type">
          {groupMode ? (
            <Pick testId="filter-entry-type-select" value={get("entry_type")} onChange={(v) => set("entry_type", v)} items={["group", "everyone"]} allLabel="Individual" labels={{ group: "Group / Team", everyone: "Both combined" }} />
          ) : (
            <Pick testId="filter-entry-type-select" value={get("entry_type")} onChange={(v) => set("entry_type", v)} items={["individual", "group"]} labels={{ individual: "Individual", group: "Group / Team" }} />
          )}
        </Field>
        <Field label="Gender">
          <MultiPick testId="filter-gender-multiselect" values={searchParams.getAll("gender")} onChange={(v) => setMulti("gender", v)} items={opts.genders || []} />
        </Field>
        <div className="col-span-2">
          <Field label="Race / Ethnicity">
            <MultiPick testId="filter-race-multiselect" values={searchParams.getAll("race")} onChange={(v) => setMulti("race", v)} items={opts.races || []} />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Event & Client Groups">
            <MultiPick testId="filter-event-group-multiselect" values={searchParams.getAll("event_group")} onChange={(v) => setMulti("event_group", v)} items={opts.event_groups || []} allLabel="All event / client groups" />
          </Field>
        </div>
        <Field label="Age min">
          <Input data-testid="filter-age-min" type="number" min="0" value={get("age_min")} onChange={(e) => set("age_min", e.target.value)} />
        </Field>
        <Field label="Age max">
          <Input data-testid="filter-age-max" type="number" min="0" value={get("age_max")} onChange={(e) => set("age_max", e.target.value)} />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        {showHistoryToggle && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Switch
              data-testid="filter-show-history"
              checked={get("history") === "true"}
              onCheckedChange={(c) => set("history", c ? "true" : "")}
            />
            Show all past entries (otherwise newest per person)
          </label>
        )}
        {opts.has_samples && (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch
            data-testid="filter-include-sample"
            checked={get("include_sample") !== "false"}
            onCheckedChange={(c) => set("include_sample", c ? "" : "false")}
          />
          Include sample entries
        </label>
        )}
        <Button data-testid="filter-reset-button" variant="ghost" size="sm" className="ml-auto" onClick={() => setSearchParams({}, { replace: true })}>
          <RotateCcw className="mr-2 h-3.5 w-3.5" /> Reset filters
        </Button>
      </div>
    </div>
  );
};
